import { works } from "@/mocks/works";
import { characters } from "@/mocks/characters";
import { charactersExtra } from "@/mocks/charactersExtra";
import { newsList } from "@/mocks/news";
import { featureList } from "@/mocks/features";
import type { Character, FeatureItem, NewsItem, Work, WorkType } from "@/types";

export const allWorks = works as Work[];
export const allCharacters = [...characters, ...charactersExtra] as Character[];
export const allNews = newsList as NewsItem[];
export const allFeatures = featureList as FeatureItem[];

export const REGIONS = ["全部地区", "中国大陆", "中国香港", "中国台湾", "日本", "韩国", "美国", "英国"];
export const YEARS = ["全部年份", "2026", "2025", "2024", "2023", "2022"];
export const RATINGS = ["全部评分", "9分以上", "8分以上", "7分以上"];
export const TYPES: ("全部类型" | WorkType)[] = ["全部类型", "电影", "电视剧", "网络剧"];

export function getWorkById(id?: string): Work | undefined {
  if (!id) return undefined;
  return allWorks.find((item) => item.id === id);
}

export function getCharacterById(id?: string): Character | undefined {
  if (!id) return undefined;
  return allCharacters.find((item) => item.id === id);
}

export function getCharactersByWork(workId: string): Character[] {
  return allCharacters.filter((item) => item.workId === workId);
}

export function getWorksByIds(ids: string[]): Work[] {
  return ids.map((id) => getWorkById(id)).filter((item): item is Work => Boolean(item));
}

export interface SearchResult {
  kind: "work" | "character";
  id: string;
  title: string;
  subtitle: string;
  workId: string;
}

export function searchAll(keyword: string): SearchResult[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  const results: SearchResult[] = [];

  allWorks.forEach((work) => {
    const haystack = [work.title, work.director, ...work.cast, ...work.genres, work.region]
      .join(" ")
      .toLowerCase();
    if (haystack.includes(key)) {
      results.push({
        kind: "work",
        id: work.id,
        title: work.title,
        subtitle: `${work.type} · ${work.year} · ${work.genres.join(" / ")}`,
        workId: work.id
      });
    }
  });

  allCharacters.forEach((character) => {
    const work = getWorkById(character.workId);
    const haystack = [character.name, character.actor, character.identity, ...character.tags]
      .join(" ")
      .toLowerCase();
    if (haystack.includes(key)) {
      results.push({
        kind: "character",
        id: character.id,
        title: character.name,
        subtitle: `角色 · 来自《${work ? work.title : "未知作品"}》`,
        workId: character.workId
      });
    }
  });

  return results;
}

export function searchWorks(keyword: string): Work[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return allWorks.filter((work) =>
    [work.title, work.director, ...work.cast, ...work.genres, work.region]
      .join(" ")
      .toLowerCase()
      .includes(key)
  );
}

/** 简单标签匹配的相关推荐 */
export function getRelatedWorks(current: Work, limit = 3): Work[] {
  const scored = allWorks
    .filter((work) => work.id !== current.id)
    .map((work) => {
      let score = 0;
      if (work.type === current.type) score += 2;
      if (work.director === current.director) score += 3;
      work.genres.forEach((genre) => {
        if (current.genres.includes(genre)) score += 2;
      });
      if (work.region === current.region) score += 1;
      return { work, score };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, limit).map((item) => item.work);
}

export interface FilterState {
  type: string;
  region: string;
  year: string;
  rating: string;
  sort: string;
}

export function filterAndSortWorks(state: FilterState): Work[] {
  let list = allWorks.slice();

  if (state.type && state.type !== "全部类型") {
    list = list.filter((work) => work.type === state.type);
  }
  if (state.region && state.region !== "全部地区") {
    list = list.filter((work) => work.region === state.region);
  }
  if (state.year && state.year !== "全部年份") {
    list = list.filter((work) => String(work.year) === state.year);
  }
  if (state.rating && state.rating !== "全部评分") {
    const threshold = state.rating === "9分以上" ? 9 : state.rating === "8分以上" ? 8 : 7;
    list = list.filter((work) => work.rating >= threshold);
  }

  if (state.sort === "最热") {
    list.sort((a, b) => b.rating - a.rating);
  } else if (state.sort === "评分") {
    list.sort((a, b) => b.rating - a.rating);
  } else {
    list.sort((a, b) => b.year - a.year);
  }

  return list;
}

export function getHotWorks(limit = 6): Work[] {
  return allWorks
    .slice()
    .sort((a, b) => b.rating - a.rating)
    .slice(0, limit);
}

export function getFeaturedNewsForHome(): NewsItem[] {
  return allNews;
}

export function formatScore(score: number): string {
  return score.toFixed(1);
}